import React from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { cn } from '@/src/infrastructure/lib/utils';
import type { ApiFeedback } from '@/src/infrastructure/lib/api';
import { FeedbackStatusBadge } from './FeedbackStatusBadge';
import { FEEDBACK_TYPE_OPTIONS } from './constants';

function typeLabel(type: ApiFeedback['type']) {
  return FEEDBACK_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Compact card used in the user's feedback history list. */
export function FeedbackCard({ feedback }: { feedback: ApiFeedback }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="bg-surface-lowest rounded-2xl border border-outline-variant/30 hover:border-primary/40 transition-colors"
    >
      <Link
        to={`/dashboard/feedback/${feedback.id}`}
        className={cn(
          'block p-5 outline-none rounded-2xl',
          'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs text-on-surface-variant">
              <span className="font-semibold uppercase tracking-wide">{typeLabel(feedback.type)}</span>
              <span>·</span>
              <span>{formatDate(feedback.createdAt)}</span>
            </div>
            <h3 className="mt-1 text-sm font-semibold text-on-surface truncate">
              {feedback.title}
            </h3>
            <p className="mt-1.5 text-sm text-on-surface-variant line-clamp-2">
              {feedback.description}
            </p>
          </div>
          <FeedbackStatusBadge status={feedback.status} />
        </div>

        {feedback.adminResponse && (
          <div className="mt-4 rounded-xl bg-primary/5 border border-primary/10 px-3 py-2 text-xs text-on-surface-variant">
            <span className="font-semibold text-primary">Support:</span>{' '}
            <span className="line-clamp-2">{feedback.adminResponse}</span>
          </div>
        )}
      </Link>
    </motion.div>
  );
}
